import { apiFetch } from "@/integrations/backend/client";

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

export type UIExercise = {
  id: string;
  order: number;
  exercise: string;
  sets: number;
  reps: number;
  time_seconds: number;
  rest_seconds: number;
  weight_kg: number;
  weight_lbs: number;
  difficulty: "Beginner" | "Intermediate" | "Expert" | "Pro";
};

export type UIGrouping = {
  id: string;
  order: number;
  sets: number;
  rest_seconds: number;
  exercises: UIExercise[];
};

export async function fetchTemplateGroupings(templateId: string): Promise<UIGrouping[]> {
  const res = await apiFetch(`/api/v1/workouts/templates/${templateId}/groupings`, { method: "GET" });
  if (!res.ok) throw new Error(`workouts_groupings_failed:${res.status}`);
  const json = (await res.json()) as unknown;
  if (!Array.isArray(json)) return [];

  return (json as Array<Record<string, unknown>>).map((g) => {
    return {
      ...(g as unknown as UIGrouping),
      exercises: asArray(g.exercises),
    } satisfies UIGrouping;
  });
}

export async function addExerciseToTemplate(templateId: string, exerciseId: string) {
  const res = await apiFetch(`/api/v1/workouts/templates/${templateId}/add-exercise`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ exercise_id: exerciseId }),
  });
  if (!res.ok) throw new Error(`workouts_add_exercise_failed:${res.status}`);
  return (await res.json()) as { grouping_id: string };
}

export async function reorderGroupings(templateId: string, groupingIdsInOrder: string[]) {
  const res = await apiFetch(`/api/v1/workouts/templates/${templateId}/reorder-groupings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ grouping_ids: groupingIdsInOrder }),
  });
  if (!res.ok) throw new Error(`workouts_reorder_groupings_failed:${res.status}`);
}

export async function reorderExercisesWithinGrouping(groupingId: string, exerciseIdsInOrder: string[]) {
  const res = await apiFetch(`/api/v1/workouts/groupings/${groupingId}/reorder-exercises`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ exercise_ids: exerciseIdsInOrder }),
  });
  if (!res.ok) throw new Error(`workouts_reorder_exercises_failed:${res.status}`);
}

export async function combineExercises(droppedExerciseId: string, targetExerciseId: string) {
  const res = await apiFetch(`/api/v1/workouts/exercises/combine`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dropped_exercise: droppedExerciseId, target_exercise: targetExerciseId }),
  });
  if (!res.ok) throw new Error(`workouts_combine_exercises_failed:${res.status}`);
}

export async function moveExerciseToGroupingEnd(exerciseId: string, targetGroupingId: string) {
  const res = await apiFetch(`/api/v1/workouts/exercises/move-to-grouping-end`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ exercise_id: exerciseId, target_grouping: targetGroupingId }),
  });
  if (!res.ok) throw new Error(`workouts_move_exercise_failed:${res.status}`);
}

export async function extractExerciseToNewGrouping(exerciseId: string, templateId: string, targetIndex: number) {
  const res = await apiFetch(`/api/v1/workouts/exercises/extract-to-new-grouping`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ exercise_id: exerciseId, template_id: templateId, target_index: targetIndex }),
  });
  if (!res.ok) throw new Error(`workouts_extract_failed:${res.status}`);
  return (await res.json()) as string;
}

export async function updateGroupingSets(groupingId: string, sets: number) {
  const res = await apiFetch(`/api/v1/workouts/groupings/${groupingId}/update-sets`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sets }),
  });
  if (!res.ok) throw new Error(`workouts_update_grouping_sets_failed:${res.status}`);
}

export async function updateGroupingRest(groupingId: string, restSeconds: number) {
  const res = await apiFetch(`/api/v1/workouts/groupings/${groupingId}/update-rest`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rest_seconds: restSeconds }),
  });
  if (!res.ok) throw new Error(`workouts_update_grouping_rest_failed:${res.status}`);
}

export async function updateExerciseFields(exerciseId: string, patch: Partial<Pick<UIExercise, "sets"|"reps"|"time_seconds"|"rest_seconds"|"weight_kg"|"weight_lbs"|"difficulty">>) {
  const res = await apiFetch(`/api/v1/workouts/exercises/${exerciseId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`workouts_update_exercise_fields_failed:${res.status}`);
}

export async function deleteExerciseAndCleanup(exerciseId: string) {
  const res = await apiFetch(`/api/v1/workouts/exercises/${exerciseId}/delete-and-cleanup`, { method: "POST" });
  if (!res.ok) throw new Error(`workouts_delete_exercise_cleanup_failed:${res.status}`);
}


